import { Slide } from "pwapi";

import {
  Annotation,
  PenDrawingAnnotation,
  ViewerMode,
} from "custom-models/ViewerTypes";

import { CaseDetailState, Modify } from "./caseDetailReducer";
import {
  ADD_ANNOTATION_REQUEST,
  ADD_ANNOTATION_SUCCESS,
  ADD_ANNOTATION_FAILURE,
  CASE_BY_ACCESSION_SUCCESS,
  CASE_BY_ACCESSION_FAILURE,
  CHANGE_VIEWER_MODE,
  CLEAR_MINOR_ERROR,
  DELETE_ANNOTATION_SUCCESS,
  DELETE_ANNOTATION_FAILURE,
  EDIT_ANNOTATION_FAILURE,
  EDIT_ANNOTATION_SUCCESS,
  FETCH_ANNOTATIONS_SUCCESS,
  FETCH_ANNOTATIONS_FAILURE,
  LOGIN_SUCCESS,
  RESET_NEW_PEN_DRAWING,
  SELECT_ANNOTATION,
  SET_EDITED_ANNOTATION,
  SLIDE_BARCODE_URL_SUCCESS,
  SLIDE_DETAIL_SUCCESS,
  SLIDE_DETAIL_FAILURE,
  START_NEW_PEN_DRAWING,
  WIPE_SLIDE_DETAIL,
  FETCH_ANNOTATIONS_REQUEST,
  SLIDE_REQUEST_RESCAN_FAILURE,
  TOGGLE_ANNOTATION_VISIBILITY,
  SLIDE_DETAIL_REQUEST,
  FLAG_CREATE_SUCCESS,
  DELETE_STAIN_ORDER_SUCCESS,
  PARTIAL_UPDATE_STAIN_ORDER_SUCCESS,
} from "../actions/constants";

// TODO: Move these extra attributes to meta.slides (or somewhere else) and replace SlideDetailState with Slide
export interface SlideDetailState
  extends Modify<
    Slide,
    {
      // Additional Fields for updated slide detail state
      viewerMode: ViewerMode;

      // Annotations for slides
      annotations?: Annotation[];
      newPenDrawing?: PenDrawingAnnotation;
      pendingAnnotationsBuffer: Annotation[];
      selectedAnnotation?: Annotation;
      editedAnnotation?: Annotation;
      annotationsHidden: boolean;

      caseByAccessionResult?: CaseDetailState;
      noMatchingCaseByAccessionResult?: boolean;

      // Various Frontend flags and objects
      error?: Error;
      minorError?: string;
      loading: boolean;
      maskBySlideId: Map<number, string | undefined>;

      // TODO: seems to be legacy field, only used in SlideList.tsx
      s3_upload_finished_at?: string;
      // undoHistory?: Annotation[][]; // not used yet

      // New Attribute for range based precompute
      // TODO: OpenAPI
      full_precompute_location?: string | null;
    }
  > {}

export const initialState: SlideDetailState = {
  viewerMode: ViewerMode.PAN_ZOOM,
  pendingAnnotationsBuffer: [],
  annotationsHidden: false,
  flags: [],
  loading: true,
  metadata: {},
  maskBySlideId: new Map<number, string>(),
};

const reducer = (
  state: SlideDetailState = initialState,
  action: any
): SlideDetailState => {
  const hygenic = new Map(state.maskBySlideId);
  hygenic.delete(state.id || 0);
  switch (action.type) {
    case SLIDE_DETAIL_SUCCESS: {
      return {
        ...state,
        ...action.payload,
        newPenDrawing: undefined,
        maskBySlideId: hygenic,
        pendingAnnotationsBuffer: [],
        selectedAnnotation: undefined,
        editedAnnotation: undefined,
        loading: false,
        // undoHistory: undefined,
      };
    }
    case SLIDE_DETAIL_REQUEST: {
      return {
        ...state,
        loading: true,
      };
    }
    case SLIDE_DETAIL_FAILURE: {
      return {
        ...state,
        error: action.payload,
        newPenDrawing: undefined,
        maskBySlideId: hygenic,
        pendingAnnotationsBuffer: [],
        selectedAnnotation: undefined,
        editedAnnotation: undefined,
        loading: false,
      };
    }
    case CASE_BY_ACCESSION_SUCCESS: {
      return {
        ...state,
        ...action.payload,
        loading: false,
      };
    }
    case CASE_BY_ACCESSION_FAILURE: {
      return {
        ...state,
        error: action.payload,
        caseByAccessionResult: undefined,
        noMatchingCaseByAccessionResult: undefined,
        loading: false,
      };
    }
    case SLIDE_BARCODE_URL_SUCCESS: {
      return {
        ...state,
        selectedAnnotation: undefined,
        editedAnnotation: undefined,
      };
    }
    case CHANGE_VIEWER_MODE: {
      return {
        ...state,
        ...action.payload,
        newPenDrawing: undefined,
        selectedAnnotation: undefined,
        editedAnnotation: undefined,
        annotationsHidden: false,
      };
    }
    case FETCH_ANNOTATIONS_REQUEST: {
      return {
        ...state,
        annotations: undefined,
      };
    }
    case FETCH_ANNOTATIONS_SUCCESS: {
      return {
        ...state,
        annotations: action.payload.annotations,
      };
    }
    case FETCH_ANNOTATIONS_FAILURE: {
      return {
        ...state,
        annotations: undefined,
        minorError: action.payload.message, // treat as nonfatal (shows dialog)
      };
    }
    case ADD_ANNOTATION_REQUEST: {
      const { pendingAnnotationsBuffer } = state;
      if (action.payload.annotation) {
        // should be same as newPenDrawing in state
        return {
          ...state,
          pendingAnnotationsBuffer: pendingAnnotationsBuffer.concat(
            action.payload.annotation
          ),
          viewerMode: ViewerMode.PAN_ZOOM,
          newPenDrawing: undefined,
          selectedAnnotation: undefined,
          editedAnnotation: undefined,
        };
      }
      return state;
    }
    case ADD_ANNOTATION_SUCCESS: {
      const { annotation } = action.payload;
      const annotations: Annotation[] = state.annotations || [];
      const pendingAnnotationsBuffer = state.pendingAnnotationsBuffer;
      // const undoHistory: Annotation[][] = (state.undoHistory===undefined ? [annotations] : state.undoHistory.concat([ annotations ]));
      return {
        ...state,
        annotations: annotations.concat([annotation]),
        pendingAnnotationsBuffer: pendingAnnotationsBuffer.filter(
          (e) => e.created_at !== annotation.created_at
        ),
        selectedAnnotation: undefined,
        editedAnnotation: undefined,
        // undoHistory,
      };
    }
    case ADD_ANNOTATION_FAILURE: {
      return {
        ...state,
        pendingAnnotationsBuffer: [],
        selectedAnnotation: undefined,
        editedAnnotation: undefined,
        minorError: action.payload.message,
      };
    }
    case DELETE_ANNOTATION_SUCCESS: {
      const { annotationId } = action.payload;
      const annotations: Annotation[] = state.annotations || [];
      const newAnnotations = annotations.filter(
        (annotation) => annotation.id !== annotationId
      );
      return {
        ...state,
        annotations: newAnnotations,
      };
    }
    case DELETE_ANNOTATION_FAILURE: {
      return {
        ...state,
        minorError: action.payload.message,
      };
    }
    case START_NEW_PEN_DRAWING: {
      const { newPenDrawing } = action.payload;
      return {
        ...state,
        newPenDrawing,
      };
    }
    case RESET_NEW_PEN_DRAWING: {
      return {
        ...state,
        newPenDrawing: undefined,
      };
    }
    case SELECT_ANNOTATION: {
      const { annotation } = action.payload;
      return {
        ...state,
        newPenDrawing: undefined,
        selectedAnnotation: annotation,
      };
    }
    case SET_EDITED_ANNOTATION: {
      const { annotation } = action.payload;
      if (annotation === undefined) {
        // Done editing or canceled editing
        return {
          ...state,
          editedAnnotation: undefined,
        };
      }
      // Started editing- set selectedAnnotation as well
      return {
        ...state,
        selectedAnnotation: annotation,
        editedAnnotation: annotation,
      };
    }
    case EDIT_ANNOTATION_FAILURE: {
      return {
        ...state,
        selectedAnnotation: undefined,
        editedAnnotation: undefined,
        minorError: action.payload.message,
      };
    }
    case EDIT_ANNOTATION_SUCCESS: {
      const annotationWithEdits = action.payload.annotation;
      const annotations: Annotation[] = state.annotations || [];
      const newAnnotations = annotations.reduce(
        (acc: Annotation[], e: Annotation) => {
          if (e.id === annotationWithEdits.id) {
            acc.push(annotationWithEdits);
          } else {
            acc.push(e);
          }
          return acc;
        },
        []
      );
      return {
        ...state,
        annotations: newAnnotations,
        selectedAnnotation: action.payload.annotation,
        editedAnnotation: undefined,
      };
    }
    case TOGGLE_ANNOTATION_VISIBILITY: {
      return {
        ...state,
        annotationsHidden: !state.annotationsHidden,
      };
    }
    case SLIDE_REQUEST_RESCAN_FAILURE:
      return {
        ...state,
        error: action.payload,
      };
    case WIPE_SLIDE_DETAIL:
      // Wipe unless the current ID is the one specified in the payload
      if (
        !action.payload.nextSlideId ||
        state.id !== action.payload.nextSlideId
      ) {
        return { ...initialState };
      }
      return state;
    case LOGIN_SUCCESS:
      return { ...state, error: undefined };
    case CLEAR_MINOR_ERROR:
      return { ...state, minorError: undefined };
    case DELETE_STAIN_ORDER_SUCCESS:
      return {
        ...state,
        lab_orders: (state.lab_orders || []).map((lab_order) =>
          lab_order.id !== action.args.id
            ? lab_order
            : {
                ...lab_order,
                ...action.payload.response,
              }
        ),
        lab_order:
          state.lab_order && state.lab_order.id === action.args.id
            ? {
                ...state.lab_order,
                ...action.payload.response,
              }
            : state.lab_order,
      };
    case PARTIAL_UPDATE_STAIN_ORDER_SUCCESS:
      return {
        ...state,
        lab_orders: (state.lab_orders || []).map((lab_order) =>
          lab_order.id !== action.payload.response.id
            ? lab_order
            : {
                ...lab_order,
                ...action.payload.response,
              }
        ),
        lab_order:
          state.lab_order && state.lab_order.id === action.payload.response.id
            ? {
                ...state.lab_order,
                ...action.payload.response,
              }
            : state.lab_order,
      };
    case FLAG_CREATE_SUCCESS:
      return {
        ...state,
        flags: [...(state.flags || []), action.payload.result],
      };
    default:
      return state;
  }
};

export default reducer;
